import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import React, { Component, createRef } from 'react';
import { MenuListProps, components } from 'react-select';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import SelectItem from '@commons/Select/SelectItem';
import { CustomSelectProps, Option } from '@commons/Select/types';

import styles from './SelectMenuList.scss';

const ITEM_SIZE = 43;

//----------------------------------------------------------------------
// TYPES
//----------------------------------------------------------------------

interface SelectMenuListProps extends MenuListProps<Option, false> {
  selectProps: Required<CustomSelectProps<false>>;
  children: Array<JSX.Element>;
}

//----------------------------------------------------------------------
// COMPONENT
//----------------------------------------------------------------------

class SelectMenuList extends Component<SelectMenuListProps> {
  //----------------------------------------------------------------------
  // PROPERTIES
  //----------------------------------------------------------------------

  listRef = createRef<VirtuosoHandle>();
  listHeight: number;

  //----------------------------------------------------------------------
  // LIFECYCLE
  //----------------------------------------------------------------------

  constructor(props: SelectMenuListProps) {
    super(props);

    const { children, options } = props;
    const itemCount = children.length || options.length;

    this.listHeight = this.calcInitialListSize(itemCount);
  }

  shouldComponentUpdate(prevProps: SelectMenuListProps) {
    const { focusedOptionIndex, options, isLoading } = this.props.selectProps;
    return (
      !isEqual(prevProps.selectProps.focusedOptionIndex, focusedOptionIndex) ||
      !isEqual(prevProps.selectProps.options, options) ||
      prevProps.selectProps.isLoading !== isLoading
    );
  }

  componentDidMount() {
    const { children, options, selectProps, isMulti } = this.props;
    const { selectedOptionIndex } = selectProps;

    const itemCount = children.length || options.length;
    this.updateListSize(itemCount);

    if (!isNil(this.listRef.current) && !isMulti) {
      this.listRef.current.scrollToIndex({ index: selectedOptionIndex!, align: 'start' });
    }
  }

  componentDidUpdate(prevProps: SelectMenuListProps) {
    const { children, options, selectProps } = this.props;
    const { focusedOptionIndex } = selectProps;

    const itemCount = children.length || options.length;
    const prevItemCount = prevProps.children.length || prevProps.options.length;

    if (
      !isNil(this.listRef.current) &&
      prevProps.selectProps.focusedOptionIndex !== focusedOptionIndex
    ) {
      this.listRef.current.scrollToIndex({ index: focusedOptionIndex, align: 'end' });
    }

    if (prevItemCount !== itemCount) {
      this.updateListSize(itemCount);
    }
  }

  //----------------------------------------------------------------------
  // UTILS
  //----------------------------------------------------------------------

  calcInitialListSize = (itemCount: number) => {
    return ITEM_SIZE * itemCount;
  };

  updateListSize = (itemCount: number) => {
    if (itemCount === 0) {
      this.listHeight = 85;
      this.forceUpdate();
    }

    if (itemCount > 10) {
      this.listHeight = 300;
      this.forceUpdate();
    }

    if (itemCount > 0 && itemCount < 10) {
      requestIdleCallback(() => {
        let listSize = 0;
        const itemsList = document.querySelectorAll('[data-known-size]');

        if (itemsList.length === itemCount) {
          itemsList.forEach((virtualizedItem) => {
            const height = Number(virtualizedItem.getAttribute('data-known-size'));
            listSize += height;
          });

          this.listHeight = listSize;
          this.forceUpdate();
        } else {
          this.listHeight = this.calcInitialListSize(itemCount);
          this.forceUpdate();
        }
      });
    }
  };

  //----------------------------------------------------------------------
  // RENDER
  //----------------------------------------------------------------------

  render() {
    const { selectProps, options, children } = this.props;

    let menuContent: JSX.Element;

    if (selectProps.isLoading) {
      menuContent = (
        <components.LoadingMessage {...this.props} className={styles.emptyState}>
          {selectProps.loadingMessage({ inputValue: selectProps.inputValue })}
        </components.LoadingMessage>
      );
    } else if (options.length === 0 || !Array.isArray(children)) {
      menuContent = (
        <components.NoOptionsMessage {...this.props} className={styles.emptyState}>
          {selectProps.noOptionsMessage({ inputValue: selectProps.inputValue })}
        </components.NoOptionsMessage>
      );
    } else {
      menuContent = (
        <Virtuoso
          style={{ height: this.listHeight }}
          totalCount={children.length}
          itemContent={(index) => <SelectItem>{children[index]}</SelectItem>}
          ref={this.listRef}
        />
      );
    }

    return (
      <components.MenuList {...this.props} className={styles.container}>
        {menuContent}
      </components.MenuList>
    );
  }
}

export default SelectMenuList;
